import { useCallback, useEffect, useMemo, useState } from "react";
import { getPaymentConfigurations } from "../services/payments";
import { useUserState } from "../state/user";
import useProfessionalAccountStatus from "./useProfessionalAccountStatus";
import {
  PaymentConfiguration,
  canConfigurationCharge
} from "../types/payments";

/**
 * Loads the professional's payment configurations.
 *
 * `easypayState` is moved by the backend and by Easypay, never by the client,
 * and there are no webhooks or polling. So the list is refreshed when the
 * window regains focus, and `refetch` is exposed for an explicit refresh
 * button — otherwise a configuration approved meanwhile would look stale.
 */
export default function usePaymentConfigurations() {
  const { token } = useUserState();
  const { paymentId, loading: loadingAccount } = useProfessionalAccountStatus();
  const [configurations, setConfigurations] = useState<PaymentConfiguration[]>(
    []
  );
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    if (!token || !paymentId) return;
    try {
      setLoading(true);
      setError(null);
      const result = await getPaymentConfigurations({
        authToken: token,
        paymentId
      });
      setConfigurations(result ?? []);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [token, paymentId]);

  useEffect(() => {
    // Wait for the profile before deciding there is nothing to load.
    if (loadingAccount) return;
    if (!token || !paymentId) {
      setLoading(false);
      return;
    }
    refetch();
  }, [loadingAccount, token, paymentId, refetch]);

  useEffect(() => {
    window.addEventListener("focus", refetch);
    return () => window.removeEventListener("focus", refetch);
  }, [refetch]);

  /** Configurations that can actually be used to charge a patient. */
  const chargeableConfigurations = useMemo(
    () => configurations.filter(canConfigurationCharge),
    [configurations]
  );

  return {
    configurations,
    chargeableConfigurations,
    loading: loading || loadingAccount,
    error,
    refetch
  };
}
